// Canonical source for all three worker apps. scripts/sync-house-ads.mjs
// generates the tools/blog copies; edit this root file only.

import type { HouseAdCreative } from '@/lib/house-ads';

/**
 * Generated banner artwork for our own products, drawn as inline SVG.
 *
 * WHY INLINE SVG rather than an image file:
 *  - Unblockable. This is the creative shown to visitors running an ad blocker,
 *    who cannot load the affiliate network's artwork and often cannot load an
 *    image from a path containing "ad", "banner" or "promo" either. Inline SVG
 *    issues no network request at all, so nothing can filter it.
 *  - Zero maintenance. There are no binary assets to commission, store, resize
 *    or keep in sync. Adding a product means adding a name, a tagline and two
 *    brand colours to lib/ads/house-ads.ts — the artwork follows.
 *  - Sharp at any density, and about a kilobyte.
 *
 * THE MOTIF is a field of orthogonal circuit traces, generated from a hash of
 * the product name. Because the constraint is that artwork must come from data,
 * the distinguishing mark has to be parametric rather than drawn — so the same
 * routine yields a different, stable layout per product. Traces were chosen
 * over a generic node cloud because they are honest to what these products do:
 * every one of them routes something from A to B — a request, an alert, an
 * idea, a session, a release — and schematic vernacular is native to the
 * developers and admins actually using these sites.
 *
 * Restraint is deliberate: the traces are the one loud element. The background
 * is a two-stop gradient from the product's own colours, type is a plain system
 * stack with weight doing the work, and there is no animation — motion in an ad
 * slot reads as a nuisance, not as craft.
 */

type Size = '300x250' | '728x90' | '160x600';

export function svgSizeFor(width: number): Size | null {
  if (width >= 728) return '728x90';
  if (width >= 300) return '300x250';
  if (width >= 160) return '160x600';
  return null;
}

/** Deterministic 32-bit hash — same product always yields the same artwork. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32 — small seeded PRNG, so trace layout is stable across renders. */
function rng(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Darken a #rrggbb toward black by `amount` (0..1), for the gradient's far stop. */
function shade(hex: string, amount: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.round(((n >> 16) & 255) * (1 - amount));
  const g = Math.round(((n >> 8) & 255) * (1 - amount));
  const b = Math.round((n & 255) * (1 - amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

interface Trace {
  d: string;
  x: number;
  y: number;
}

/**
 * Build orthogonal traces across a box. Each starts at the left edge of the
 * field, steps right in two or three right-angled moves, and terminates in a
 * node — the same shape a signal takes across a board.
 */
function buildTraces(
  seed: number,
  box: { x: number; y: number; w: number; h: number },
  count: number,
): Trace[] {
  const rand = rng(seed);
  const out: Trace[] = [];
  for (let i = 0; i < count; i++) {
    const startY = box.y + ((i + 0.5) / count) * box.h;
    const midX = box.x + box.w * (0.25 + rand() * 0.4);
    const endY = box.y + rand() * box.h;
    const endX = box.x + box.w * (0.7 + rand() * 0.3);
    out.push({
      d: `M ${box.x.toFixed(1)} ${startY.toFixed(1)} H ${midX.toFixed(1)} V ${endY.toFixed(1)} H ${endX.toFixed(1)}`,
      x: endX,
      y: endY,
    });
  }
  return out;
}

export default function HouseAdSvg({
  creative,
  size,
}: {
  creative: HouseAdCreative;
  size: Size;
}) {
  const [brand, accent] = creative.colors;
  const [w, h] = size.split('x').map(Number);
  const seed = hash(creative.name);

  // Gradient ids must be unique per rendered instance: several house ads can
  // share a page, and duplicate ids would make every banner adopt the first
  // one's gradient.
  const uid = `ha-${creative.trackingId}-${size}`;

  const wide = size === '728x90';
  const tall = size === '160x600';

  // Field for the traces, and the type block, per shape.
  const field = wide
    ? { x: w * 0.6, y: 8, w: w * 0.4, h: h - 16 }
    : tall
      ? { x: 0, y: 0, w, h: h * 0.5 }
      : { x: w * 0.42, y: 0, w: w * 0.58, h: h * 0.62 };
  const traces = buildTraces(seed, field, wide ? 4 : tall ? 7 : 5);

  const pad = wide ? 22 : 18;
  // The 160-wide format cannot fit most product names on one line, and SVG will
  // happily draw straight past the edge rather than wrap. Split on the last
  // space that fits.
  const nameLines: string[] = [];
  if (tall && creative.name.length > 13) {
    const parts = creative.name.split(' ');
    let cur = '';
    for (const word of parts) {
      if ((cur + ' ' + word).trim().length > 13 && cur) {
        nameLines.push(cur.trim());
        cur = word;
      } else cur = (cur + ' ' + word).trim();
    }
    if (cur) nameLines.push(cur.trim());
  } else {
    nameLines.push(creative.name);
  }
  const nameSize = wide ? 21 : tall ? 17 : 25;
  const bodySize = wide ? 12 : tall ? 11 : 13;
  const nameY = wide ? 40 : tall ? h * 0.5 + 46 : 150;

  // SVG has no text reflow, so wrap by character budget for the shape.
  const perLine = wide ? 52 : tall ? 17 : 30;
  const maxLines = wide ? 1 : tall ? 5 : 3;
  const lines: string[] = [];
  let line = '';
  for (const word of creative.tagline.split(' ')) {
    if ((line + ' ' + word).trim().length > perLine) {
      if (lines.length + 1 >= maxLines && line) break;
      lines.push(line.trim());
      line = word;
    } else {
      line = (line + ' ' + word).trim();
    }
  }
  if (line && lines.length < maxLines) lines.push(line.trim());

  const font =
    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`${creative.name} — ${creative.tagline}`}
      className="mx-auto block h-auto max-w-full"
    >
      <defs>
        <linearGradient id={`${uid}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={brand} />
          <stop offset="100%" stopColor={shade(brand, 0.45)} />
        </linearGradient>
        {/* Fades the trace field out toward the type so the two never fight. */}
        <linearGradient
          id={`${uid}-fade`}
          x1={wide || !tall ? '1' : '0'}
          y1={tall ? '0' : '0'}
          x2={wide || !tall ? '0' : '0'}
          y2={tall ? '1' : '0'}
        >
          <stop offset="0%" stopColor={accent} stopOpacity="0.55" />
          <stop offset="100%" stopColor={accent} stopOpacity="0.05" />
        </linearGradient>
      </defs>

      <rect width={w} height={h} rx="10" fill={`url(#${uid}-bg)`} />

      {/* Signature: seeded circuit traces. */}
      <g fill="none" stroke={`url(#${uid}-fade)`} strokeWidth="1.25">
        {traces.map((t, i) => (
          <path key={i} d={t.d} />
        ))}
      </g>
      {traces.map((t, i) => (
        <circle
          key={`n${i}`}
          cx={t.x}
          cy={t.y}
          r={i === 0 ? 3.5 : 2}
          fill={accent}
          opacity={i === 0 ? 0.95 : 0.4}
        />
      ))}
      {/* One trace carried at full strength — the eye needs a single entry
          point, and a uniformly weighted field reads as wallpaper. */}
      <path d={traces[0].d} fill="none" stroke={accent} strokeWidth="1.75" opacity="0.9" />

      <text
        x={pad}
        y={nameY - nameSize - 8}
        fill={accent}
        fontSize="9.5"
        fontWeight="600"
        letterSpacing="1.4"
        fontFamily={font}
      >
        INVENTIVE HQ
      </text>
      {nameLines.map((nl, i) => (
        <text
          key={`nm${i}`}
          x={pad}
          y={nameY + i * (nameSize + 2)}
          fill="#ffffff"
          fontSize={nameSize}
          fontWeight="800"
          letterSpacing="-0.4"
          fontFamily={font}
        >
          {nl}
        </text>
      ))}
      {lines.map((text, i) => (
        <text
          key={i}
          x={pad}
          y={nameY + (nameLines.length - 1) * (nameSize + 2) + 20 + i * (bodySize + 4)}
          fill="#ffffff"
          fillOpacity="0.82"
          fontSize={bodySize}
          fontFamily={font}
        >
          {text}
        </text>
      ))}

      {/* CTA as a drawn control rather than bare text — in a slot where a real
          ad failed to sell, the placement still has to read as clickable. */}
      {/* CTA position differs per shape rather than always pinning to the
          bottom-left. On the 728x90 strip the bottom edge is only 30px below
          the tagline, so a pinned button collided with it — it sits centred on
          the right instead, where the strip has room. On 160x600 pinning left a
          ~215px void, so it follows the copy. */}
      {(() => {
        const cw = wide ? 96 : 104;
        const ch = wide ? 24 : 26;
        const cx = wide ? w - cw - pad : pad;
        // Follow the copy rather than pinning to the bottom, then clamp inside
        // the canvas. A pinned CTA left a void on 160x600 and came within a few
        // pixels of a three-line tagline on 300x250 (Wide Area Intelligence is
        // the worst case).
        const flow =
          nameY +
          (nameLines.length - 1) * (nameSize + 2) +
          20 +
          lines.length * (bodySize + 4) +
          10;
        const cy = wide ? (h - ch) / 2 : Math.min(flow, h - ch - 12);
        return (
          <g>
            <rect x={cx} y={cy} width={cw} height={ch} rx={ch / 2} fill={accent} />
            <text
              x={cx + cw / 2}
              y={cy + ch / 2 + 4}
              fill={shade(brand, 0.55)}
              fontSize={wide ? 11 : 12}
              fontWeight="700"
              textAnchor="middle"
              fontFamily={font}
            >
              Take a look
            </text>
          </g>
        );
      })()}
    </svg>
  );
}
